import React from 'react';
import { Eye, Download, Trash2 } from 'lucide-react';
import {
    formatDisplayDate,
    getStatusValue,
    getStatusClass,
    getStatusLabel,
    isExceededTimeline,
    isDueSoon
} from '../constants/proposalUtils';

const ProposalsGrid = ({
    data,
    selectedIds,
    onToggleSelect,
    onActionClick
}) => {
    if (data.length === 0) {
        return <div className="proposals-empty">No proposals found.</div>;
    }

    return (
        <div className="proposals-grid">
            {data.map((prop, idx) => (
                <ProposalCard
                    key={prop._id || prop.rfp_id || prop.id || idx}
                    prop={prop}
                    isSelected={selectedIds.some(item => String(item) === String(prop._id || prop.rfp_id || prop.id))}
                    onToggleSelect={onToggleSelect}
                    onActionClick={onActionClick}
                    style={{ animationDelay: `${idx * 0.05}s` }}
                />
            ))}
        </div>
    );
};

const ProposalCard = ({ prop, isSelected, onToggleSelect, onActionClick, style }) => {
    const rfpId = prop._id || prop.rfp_id || prop.id;
    const isExceeded = isExceededTimeline(prop.proposal_deadline || prop.extraction_data?.submission_deadline);
    const isSoon = isDueSoon(prop.proposal_deadline || prop.extraction_data?.submission_deadline);
    const rawStatus = prop.proposal_stage || getStatusValue(prop);
    const statusCls = getStatusClass(rawStatus);
    const statusLabel = rawStatus ? getStatusLabel(rawStatus) : 'Draft';
    const title = prop.proposal_title || prop.extraction_data?.proposal_title || 'Untitled Proposal';
    const client = prop.client_name || prop.extraction_data?.client_name || 'Unknown Client';

    const s = (rawStatus || '').toLowerCase();
    const isFinalStatus = s.includes('submit') || s.includes('accept') || s.includes('reject');
    const shouldShowUrgency = !isFinalStatus && (isExceeded || isSoon);

    const proposalDateVal = prop.proposal_deadline && prop.proposal_deadline !== 'Not Specified' ? prop.proposal_deadline : (prop.extraction_data?.proposal_deadline || prop.extraction_data?.submission_deadline || 'N/A');
    const valValue = prop.valuation || prop.budget || prop.extraction_data?.valuation || '';

    const isWon = String(rawStatus).toLowerCase().includes('won') || String(rawStatus).toLowerCase().includes('accepted') || prop.global_status?.code === 6;
    const isLost = String(rawStatus).toLowerCase().includes('lost') || String(rawStatus).toLowerCase().includes('rejected') || prop.global_status?.code === 7;

    return (
        <div
            className={`proposal-card ${isSelected ? 'selected' : ''} ${shouldShowUrgency ? 'urgent-card' : ''}`}
            onClick={(e) => onActionClick(e, 'view', prop)}
            style={style}
        >
            <div className="proposal-card-thumbnail">
                <div
                    className={`card-selection-check ${isSelected ? 'checked' : ''}`}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelect(rfpId);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggleSelect(rfpId);
                        }
                    }}
                >
                    <div className="check-inner">
                        {isSelected && <div className="check-mark" />}
                    </div>
                </div>

                {/* Status Badge Overlay on Thumbnail */}
                <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    zIndex: 10
                }}>
                    <span className={`prop-status-pill ${statusCls}`}>
                        {statusLabel}
                    </span>
                </div>

                {!isFinalStatus && isExceeded && <div className="exceeded-timeline-badge">EXCEEDED TIMELINE</div>}
                {!isFinalStatus && !isExceeded && isSoon && <div className="due-soon-badge">DUE SOON</div>}

                <div className="thumbnail-placeholder">
                    {prop.primary_doc_url ? (
                        <div className="card-preview-frame">
                            <iframe
                                src={`${prop.primary_doc_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                title="Document Preview"
                                className="proposal-preview-iframe"
                                frameBorder="0"
                                scrolling="no"
                            />
                            <div className="preview-overlay-glass"></div>
                        </div>
                    ) : prop.documents && prop.documents[0]?.url ? (
                        <div className="card-preview-frame">
                            <iframe
                                src={`${prop.documents[0].url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                title="Document Preview"
                                className="proposal-preview-iframe"
                                frameBorder="0"
                                scrolling="no"
                            />
                            <div className="preview-overlay-glass"></div>
                        </div>
                    ) : prop.url ? (
                        <div className="card-preview-frame">
                            <iframe
                                src={`${prop.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                                title="Document Preview"
                                className="proposal-preview-iframe"
                                frameBorder="0"
                                scrolling="no"
                            />
                            <div className="preview-overlay-glass"></div>
                        </div>
                    ) : (
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="1.5" style={{ opacity: 0.6 }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                    )}
                </div>

                <div className="card-hover-actions">
                    <button className="hover-action-btn view-btn" onClick={(e) => onActionClick(e, 'view', prop)} title="View Details">
                        <Eye size={14} />
                    </button>
                    <button className="hover-action-btn download-btn" onClick={(e) => onActionClick(e, 'export', prop)} title="Download">
                        <Download size={14} />
                    </button>
                    <button className="hover-action-btn delete-btn" onClick={(e) => onActionClick(e, 'delete', prop)} title="Delete">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            <div className="proposal-card-body">
                {/* Row 1: Proposal Title (Full Width) */}
                <div className="proposal-card-title" title={title} style={{
                    marginBottom: '4px',
                    fontSize: '0.8rem',
                    lineHeight: '1.25',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    width: '100%',
                    fontWeight: '700',
                    color: '#3D4247'
                }}>{title}</div>

                {/* Row 2: Client Name & Uploaded Date */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginBottom: '4px',
                    gap: '20px'
                }}>
                    <div className="proposal-card-client" title={client} style={{
                        fontSize: '0.7rem',
                        color: '#64748B',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>{client}</div>

                    <div style={{
                        fontSize: '0.65rem',
                        color: '#94A3B8',
                        textAlign: 'right',
                        fontWeight: '500'
                    }}>
                        Uploaded: <span style={{ fontWeight: '700', color: '#64748B' }}>{formatDisplayDate(prop.created_at || prop.uploaded_at)}</span>
                    </div>
                </div>

                {/* Row 3: Valuation (Full Width) */}
                {(prop.valuation_display || prop.valuation_value || valValue) && (
                    <div className="proposal-card-valuation" style={{
                        marginBottom: '4px',
                        color: '#4167BF',
                        fontWeight: '700',
                        fontSize: '0.72rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}>
                        <span style={{ color: '#64748B', fontWeight: '500', fontSize: '0.68rem' }}>Valuation:</span>
                        {prop.valuation_display || (prop.valuation_value ? `${prop.currency || 'AED'} ${prop.valuation_value}` : valValue)}
                    </div>
                )}

                {/* Footer: Due Date (Mapped to Proposal Deadline) & Creator Info */}
                <div className="proposal-card-footer" style={{ marginTop: 'auto' }}>
                    <span className="proposal-due-date" style={{ fontSize: '0.68rem' }}>
                        {isWon && prop.completed_at ? (
                            <>Completed: <strong style={{ color: '#10B981' }}>{formatDisplayDate(prop.completed_at)}</strong></>
                        ) : isLost && prop.completed_at ? (
                            <>Rejected: <strong style={{ color: '#EF4444' }}>{formatDisplayDate(prop.completed_at)}</strong></>
                        ) : (
                            <>Due: <strong className={(isExceeded || isSoon) ? 'text-red' : ''}>{formatDisplayDate(proposalDateVal)}</strong></>
                        )}
                    </span>
                    <div className="prop-creator-info">
                        <span className="prop-creator-name" style={{ fontSize: '0.68rem' }}>{prop.created_by || 'Meghana V'}</span>
                        {prop.created_by_designation && (
                            <span className="prop-creator-designation" style={{ fontSize: '0.62rem' }}>{prop.created_by_designation}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProposalsGrid;
